import { Tween } from "@modules/app/tween";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { AskMessage, CollisionFacet, CollisionShapeLocalTransformFacet, checkQuaternionNear, checkVector3Near, convertToNumber3, convertToNumber4, DisableAllGesturesMessage, LocalTransformFacet, ModuleId, PrimitiveShape, MeshGroupFacet, worldToLocalTransform, Transform } from "flavr-common";
import { DestinationChangedMessage, DestinationReachedMessage, FlyToDestinationMessage, RequestPlayerWorldTransformMessage, ResponsePlayerWorldTransformMessage, SetPlayerWorldTransformMessage, UpdatePlatformTransformMessage } from "@messages/locomotion/LocomotionMessages";
import { modelPool, NO_MESSAGING, LOCAL_MESSAGING } from "@modules/model";
import { ensureLocalPlayerHeadModel, ensureLocalPlayerRootModel, hasLocalPlayerClientIdx } from "@modules/player";
import { Module } from "@modules/module";
import { ROOT_SCALE } from "@modules/rendering/rootScale";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { renderManager } from "@modules/xr/RenderManager";
import { camera } from "@modules/rendering/camera";
import { navMeshService } from "@modules/navMesh";
import { localPlayerContainer, PlatformScaleMode } from "./LocalPlayerContainer";
// how fast (in unit per s) are we flying to a selected object
const DEFAULT_FLY_VELOCITY = 10;
export const SCALE_DEFAULT = ROOT_SCALE;
// don't strain the garbage-collector!
const tempPosition = new Vector3();
const tempLookAtTarget = new Vector3();
const tempCameraPosition = new Vector3();
const tempCameraDirection = new Vector3();
const tempCameraQuaternion = new Quaternion();
const tempWorldPosition = new Vector3();
const tempMat41 = new Matrix4();
const tempMat42 = new Matrix4();
export const SCALE_MIN = 0.01;
export const SCALE_MAX = 100;
const LOCAL_PLAYER_TRANSFORM_EPSILON = 1e-5;
const LOCAL_PLAYER_HEAD_COLLIDER_TRANSFORM = new Matrix4().makeScale(0.5, 0.5, 0.5);
const tempLocalTransform = new Matrix4();
/**
 * Maintains the local player transform and movement state so locomotion, teleportation and head-collision updates all operate on one shared source of truth.
 *
 * Messages:
 * - `FlyToDestinationMessage` - Requests an animated locomotion move (tween) towards a target camera position, answering with `DestinationReachedMessage` or `DestinationChangedMessage` once it completes or is interrupted.
 * - `SetPlayerWorldTransformMessage` - Directly sets the player's world position, yaw angle and/or platform scale, handling XR and desktop presentation differently.
 * - `UpdatePlatformTransformMessage` - Applies incremental platform position, rotation, height and scale changes to the local player container.
 */
export class LocalPlayerModule extends Module {
    /**
     * Sets up the previous/current transform buffers used for head-collision tracking and subscribes to the
     * player transform and locomotion messages this module handles.
     */
    constructor() {
        super(ModuleId.LOCOMOTION);
        this.pose = new Vector3();
        this.lastHeadPosition = [Number.NaN, Number.NaN, Number.NaN];
        this.lastHeadOrientation = [Number.NaN, Number.NaN, Number.NaN, Number.NaN];
        this.lastHeadScale = [Number.NaN, Number.NaN, Number.NaN];
        this.handleSetPlayerWorldTransformMessage = (msg, askId) => {
            const isXRPresenting = renderManager.isXRPresenting();
            if (isXRPresenting) {
                if (msg.trans.position !== undefined) {
                    localPlayerContainer.setPosition(new Vector3().fromArray(msg.trans.position));
                }
                if (msg.trans.angle !== undefined) {
                    localPlayerContainer.setPlatformYawAngle(msg.trans.angle);
                }
                if (msg.trans.scale !== undefined) {
                    const scaleFactor = msg.trans.scale / localPlayerContainer.getPlatformScale();
                    localPlayerContainer.scalePlatform(scaleFactor, SCALE_MAX, SCALE_MIN, PlatformScaleMode.KEEP_PLATFORM_POSITION_CONSTANT);
                }
                navMeshService.onShouldRecomputeDelegate.invoke();
            }
            else {
                let shouldUpdate = false;
                if (msg.trans.position !== undefined) {
                    tempWorldPosition.set(0, 0, 0);
                    tempWorldPosition.y += msg.trans.height !== undefined ? msg.trans.height : 1.7;
                    const shouldPreserveDesktopViewDirection = msg.trans.angle === undefined;
                    if (shouldPreserveDesktopViewDirection) {
                        tempCameraQuaternion.copy(camera.perspectiveCam.quaternion);
                    }
                    localPlayerContainer.setPosition(new Vector3().fromArray(msg.trans.position));
                    camera.perspectiveCam.position.copy(tempWorldPosition);
                    if (shouldPreserveDesktopViewDirection) {
                        camera.perspectiveCam.quaternion.copy(tempCameraQuaternion);
                    }
                    else {
                        camera.perspectiveCam.quaternion.identity();
                    }
                    camera.perspectiveCam.updateMatrix();
                    shouldUpdate = true;
                }
                if (msg.trans.angle !== undefined) {
                    localPlayerContainer.setPlatformYawAngle(msg.trans.angle);
                    shouldUpdate = true;
                }
                if (msg.trans.scale !== undefined) {
                    const scaleFactor = msg.trans.scale / localPlayerContainer.getPlatformScale();
                    localPlayerContainer.scalePlatform(scaleFactor, SCALE_MAX, SCALE_MIN, PlatformScaleMode.KEEP_PLATFORM_POSITION_CONSTANT);
                    shouldUpdate = true;
                }
                if (shouldUpdate)
                    navMeshService.onShouldRecomputeDelegate.invoke();
            }
            if (askId !== undefined) {
                this.connection.answer(askId, new DestinationReachedMessage());
            }
        };
        this.previousTransform = new Transform();
        this.currentTransform = new Transform();
        this.connection.subscribeByTypeOrAskRequest(SetPlayerWorldTransformMessage, this.handleSetPlayerWorldTransformMessage);
        this.connection.subscribeForAskRequest(RequestPlayerWorldTransformMessage, (_msg, askId) => {
            const direction = new Vector3(0, 0, 1).applyQuaternion(localPlayerContainer.getOrientation());
            this.connection.answer(askId, new ResponsePlayerWorldTransformMessage({
                position: localPlayerContainer.getPosition().toArray(),
                angle: Math.atan2(direction.x, direction.z),
                scale: localPlayerContainer.getPlatformScale(),
                height: camera.perspectiveCam.position.y
            }));
        });
        this.connection.subscribe([FlyToDestinationMessage.typestatic, UpdatePlatformTransformMessage.typestatic], (message) => {
            const msg = AskMessage.is(message) ? message.message : message;
            const askId = AskMessage.is(message) ? message.askId : undefined;
            if (FlyToDestinationMessage.is(msg)) {
                this.flyToDestination(msg, askId);
            }
            else if (UpdatePlatformTransformMessage.is(msg)) {
                this.handleUpdatePlatformTransformMessage(msg);
            }
        });
    }
    /**
     * Registers the per-frame update that keeps the local player head collision model synchronized with the current camera transform.
     */
    init() {
        animationFrame$.onMovementUpdate$.add(this.updateLocalPlayerHeadCollisionModel.bind(this));
    }
    /**
     * Rebuilds and updates the local player head collision model whenever the camera pose changes enough to require a new collision transform.
     */
    updateLocalPlayerHeadCollisionModel() {
        if (!hasLocalPlayerClientIdx())
            return;
        if (this.localPlayerHeadModel === undefined) {
            this.createHeadCollisionModel();
            if (this.localPlayerHeadModel === undefined)
                return;
        }
        const cameraTransform = this.getCameraWorldTransform();
        const headPositionUnchanged = checkVector3Near(cameraTransform.position, this.lastHeadPosition, LOCAL_PLAYER_TRANSFORM_EPSILON);
        const headOrientationUnchanged = checkQuaternionNear(cameraTransform.orientation, this.lastHeadOrientation, LOCAL_PLAYER_TRANSFORM_EPSILON);
        const headScaleUnchanged = checkVector3Near(cameraTransform.scale, this.lastHeadScale, LOCAL_PLAYER_TRANSFORM_EPSILON);
        // We only send messages if the camera has moved
        if (headPositionUnchanged && headOrientationUnchanged && headScaleUnchanged)
            return;
        this.lastHeadPosition[0] = cameraTransform.position[0];
        this.lastHeadPosition[1] = cameraTransform.position[1];
        this.lastHeadPosition[2] = cameraTransform.position[2];
        this.lastHeadOrientation[0] = cameraTransform.orientation[0];
        this.lastHeadOrientation[1] = cameraTransform.orientation[1];
        this.lastHeadOrientation[2] = cameraTransform.orientation[2];
        this.lastHeadOrientation[3] = cameraTransform.orientation[3];
        this.lastHeadScale[0] = cameraTransform.scale[0];
        this.lastHeadScale[1] = cameraTransform.scale[1];
        this.lastHeadScale[2] = cameraTransform.scale[2];
        this.previousTransform.matrix = this.currentTransform.getMatrix(tempMat42);
        worldToLocalTransform(cameraTransform.matrix, cameraTransform.matrix, tempLocalTransform);
        const localPlayerRoot = ensureLocalPlayerRootModel();
        this.localPlayerHeadModel.setTransform(tempLocalTransform, NO_MESSAGING);
        localPlayerRoot.setTransform(cameraTransform.matrix, { handleDescendantTransforms: true });
        modelPool.flushMessages();
    }
    /**
     * Creates the head collision model and initializes its collision, mesh-group and local-transform facets from the current camera transform.
     */
    createHeadCollisionModel() {
        const cameraTransform = this.getCameraWorldTransform();
        const localTransformFacet = new LocalTransformFacet(cameraTransform.position, cameraTransform.orientation, cameraTransform.scale);
        this.localPlayerHeadModel = ensureLocalPlayerHeadModel();
        this.localPlayerHeadModel.setFacet(new CollisionFacet(PrimitiveShape.Box, false, true), LOCAL_MESSAGING);
        this.localPlayerHeadModel.setFacet(new CollisionShapeLocalTransformFacet(LOCAL_PLAYER_HEAD_COLLIDER_TRANSFORM.toArray()), LOCAL_MESSAGING);
        this.localPlayerHeadModel.setFacet(new MeshGroupFacet(), LOCAL_MESSAGING);
        this.localPlayerHeadModel.setFacet(localTransformFacet, LOCAL_MESSAGING);
        this.localPlayerHeadModel.setTransform(cameraTransform.matrix, LOCAL_MESSAGING);
        this.localPlayerHeadModel.flushMessages();
    }
    flyToDestination(message, askId) {
        var _a, _b, _c, _d, _e;
        this.connection.send(new DisableAllGesturesMessage(true));
        navMeshService.enabled = false;
        const currentScale = localPlayerContainer.getPlatformScale();
        const currentCameraPosition = tempPosition.copy(camera.perspectiveCam.position);
        const currentLookAtTarget = tempLookAtTarget.copy(localPlayerContainer.cameraWorldViewDirection).add(localPlayerContainer.cameraWorldPosition);
        const current = {
            scale: currentScale,
            cameraX: currentCameraPosition.x,
            cameraY: currentCameraPosition.y,
            cameraZ: currentCameraPosition.z,
            lookAtX: currentLookAtTarget.x,
            lookAtY: currentLookAtTarget.y,
            lookAtZ: currentLookAtTarget.z
        };
        const targetScale = (_a = message.params.targetScale) !== null && _a !== void 0 ? _a : currentScale * ((_b = message.params.scaleFactor) !== null && _b !== void 0 ? _b : 1);
        const targetCameraPosition = tempCameraPosition.fromArray(message.targetCameraWorldPosition);
        const cameraDirection = tempCameraDirection.subVectors(currentLookAtTarget, currentCameraPosition).normalize();
        const targetLookAtTarget = cameraDirection.multiplyScalar(targetScale).add(targetCameraPosition);
        if (message.params.lookAt !== undefined) {
            targetLookAtTarget.fromArray(message.params.lookAt);
        }
        const target = {
            scale: targetScale,
            cameraX: targetCameraPosition.x,
            cameraY: targetCameraPosition.y,
            cameraZ: targetCameraPosition.z,
            lookAtX: targetLookAtTarget.x,
            lookAtY: targetLookAtTarget.y,
            lookAtZ: targetLookAtTarget.z
        };
        const isVR = renderManager.isXRPresenting();
        // in vr mode:
        // camera = pose * scale + movement => pose = (camera - movement) / scale
        // else: pose is (0,0,0)
        if (isVR) {
            this.pose.subVectors(currentCameraPosition, localPlayerContainer.getPosition(tempLookAtTarget)).divideScalar(currentScale);
        }
        else {
            this.pose.setScalar(0);
            camera.perspectiveCam.position.set(0, 0, 0);
            localPlayerContainer.setPosition(currentCameraPosition);
        }
        (_c = this.teleportTween) === null || _c === void 0 ? void 0 : _c.stop();
        const velocity = (_d = message.params.velocity) !== null && _d !== void 0 ? _d : DEFAULT_FLY_VELOCITY;
        const distance = targetCameraPosition.distanceTo(currentCameraPosition);
        const durationInSeconds = (_e = message.params.durationInSeconds) !== null && _e !== void 0 ? _e : distance / velocity;
        const durationInMS = 1000 * durationInSeconds;
        this.teleportTween = new Tween(current)
            .to(target, durationInMS)
            .onUpdate((params) => {
            const newPlatformPosition = tempPosition.set(params.cameraX, params.cameraY, params.cameraZ).addScaledVector(this.pose, -params.scale);
            localPlayerContainer.setScale(params.scale);
            localPlayerContainer.movePlatform(newPlatformPosition.sub(localPlayerContainer.getPosition(tempLookAtTarget)));
            camera.perspectiveCam.updateMatrix();
            if (!isVR) {
                camera.perspectiveCam.lookAt(new Vector3(params.lookAtX, params.lookAtY, params.lookAtZ));
            }
        })
            .onComplete(() => {
            delete this.teleportTween;
            this.connection.send(new DestinationReachedMessage(), askId);
            this.connection.send(new DisableAllGesturesMessage(false));
            navMeshService.enabled = true;
            navMeshService.onShouldRecomputeDelegate.invoke();
        })
            .onStop(() => {
            delete this.teleportTween;
            this.connection.send(new DestinationChangedMessage(), askId);
            navMeshService.enabled = true;
            navMeshService.onShouldRecomputeDelegate.invoke();
        })
            .start();
    }
    handleUpdatePlatformTransformMessage(msg) {
        if (msg.trans.position !== undefined) {
            localPlayerContainer.moveWorld(tempWorldPosition.fromArray(msg.trans.position));
        }
        if (msg.trans.angle !== undefined) {
            localPlayerContainer.rotateWorld(msg.trans.angle);
        }
        if (msg.trans.height !== undefined) {
            localPlayerContainer.setPosition(localPlayerContainer.getPosition(tempWorldPosition).setY(msg.trans.height));
        }
        if (msg.trans.scale !== undefined) {
            const scaleFactor = msg.trans.scale / localPlayerContainer.getPlatformScale();
            localPlayerContainer.scalePlatform(scaleFactor, SCALE_MAX, SCALE_MIN, PlatformScaleMode.KEEP_USER_FEET_POSITION_CONSTANT);
        }
    }
    /// Transform helper methods ///
    getCameraWorldTransform() {
        this.currentTransform.position = localPlayerContainer.cameraWorldPosition;
        this.currentTransform.orientation = localPlayerContainer.cameraWorldOrientation;
        this.currentTransform.scale = localPlayerContainer.localPlayerRootNode.scale;
        return {
            position: convertToNumber3(localPlayerContainer.cameraWorldPosition),
            orientation: convertToNumber4(localPlayerContainer.cameraWorldOrientation),
            scale: convertToNumber3(localPlayerContainer.localPlayerRootNode.scale),
            matrix: this.currentTransform.getMatrix(tempMat41),
            transform: this.currentTransform
        };
    }
}
